import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import axios from 'axios';
import type { ApiError } from '@/types/api';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Extracts a user-facing message from an API error, falling back to a
 * generic message when the response doesn't match the expected shape
 * (e.g. network failure, unexpected error body).
 *
 * [statusOverrides] takes priority over the server's own message for a
 * matching status code — for endpoints where the backend's message is a
 * hardcoded English string not meant for direct display (e.g. bad-credentials
 * responses across login/change-password/delete-account, all "Invalid email
 * or password"-style text regardless of the client's language).
 */
export function getApiErrorMessage(
  error: unknown,
  fallback: string,
  statusOverrides?: Record<number, string>,
): string {
  if (axios.isAxiosError<ApiError>(error)) {
    const status = error.response?.status;
    const override = status !== undefined ? statusOverrides?.[status] : undefined;
    if (override !== undefined) return override;

    const message = error.response?.data?.message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return fallback;
}
